'use client';

import { Typography } from 'lighthouse';

import { SafeImage } from '@/widgets/SafeImage';
import ScrollFade from '@/widgets/ScrollFade';

import { ATTRIBUTE_FILTER_COPY } from '@/utilities/attributeFilters/constants';

import { AttributeCategoryNavProps } from './models';

import { ArrowLeft2, Category2 } from 'iconsax-react';

const FALLBACK_IMAGE = '/images/noImage/noImage.png';

export const AttributeCategoryNav = ({
    items,
    activeId,
    onSelect,
    breadcrumb,
    onBreadcrumbSelect,
    hideBreadcrumb = false
}: AttributeCategoryNavProps) => {
    if (!items.length && !breadcrumb?.length) {
        return null;
    }

    const activeItem = items.find((item) => item.id === activeId);
    const trail = hideBreadcrumb
        ? []
        : breadcrumb?.length
          ? breadcrumb
          : activeItem
            ? [{ label: ATTRIBUTE_FILTER_COPY.categoryNavTitle }, { id: activeItem.id, label: activeItem.label }]
            : [];

    return (
        <div className='space-y-2'>
            {trail.length ? (
                <nav aria-label={ATTRIBUTE_FILTER_COPY.categoryNavTitle} className='flex min-w-0 items-center gap-1.5 px-1'>
                    <Category2 size={16} color='currentColor' className='shrink-0 text-primary' variant='Bold' aria-hidden />
                    <ol className='m-0 flex min-w-0 list-none flex-wrap items-center gap-y-1 p-0'>
                        {trail.map((item, index) => {
                            const isLast = index === trail.length - 1;
                            const isClickable = Boolean(!isLast && item.id && onBreadcrumbSelect);

                            return (
                                <li key={`${item.id ?? item.label}-${index}`} className='flex min-w-0 items-center'>
                                    {index > 0 ? (
                                        <ArrowLeft2 size={12} className='mx-1 shrink-0 text-blackLowEmphasis' color='currentColor' aria-hidden />
                                    ) : null}
                                    {isClickable ? (
                                        <button
                                            type='button'
                                            onClick={() => onBreadcrumbSelect?.(item.id!)}
                                            className='min-w-0 cursor-pointer truncate border-0 bg-transparent p-0 text-right text-blackMediumEmphasis
                                                outline-none transition-colors hover:text-primary'>
                                            <Typography variant='caption' component='span' className='font-medium'>
                                                {item.label}
                                            </Typography>
                                        </button>
                                    ) : (
                                        <Typography
                                            variant='caption'
                                            component='span'
                                            color={isLast ? 'primary' : 'blackMediumEmphasis'}
                                            className={`min-w-0 truncate ${isLast ? 'font-bold' : 'font-medium'}`}>
                                            {item.label}
                                        </Typography>
                                    )}
                                </li>
                            );
                        })}
                    </ol>
                </nav>
            ) : null}

            {items.length ? (
                <ScrollFade
                    axis='horizontal'
                    className='-mx-1'
                    scrollerClassName='flex w-full flex-nowrap gap-2 overflow-x-auto px-1 py-1 [-ms-overflow-style:none] [scrollbar-width:none] md:gap-3
                            [&::-webkit-scrollbar]:hidden'>
                    {items.map((item) => {
                        const isActive = activeId === item.id;

                        return (
                            <button
                                type='button'
                                key={item.id}
                                aria-pressed={isActive}
                                onClick={() => onSelect(item.id)}
                                className={`group relative flex min-h-12 min-w-32 shrink-0 cursor-pointer items-center gap-1.5 rounded-lg border
                                    border-solid px-2 py-1.5 text-right transition-colors duration-200 md:min-h-14 md:min-w-40 md:gap-2 md:px-3 ${
                                        isActive
                                            ? 'border-primary/40 bg-white shadow-sm'
                                            : 'border-N200 bg-white hover:border-primary/30 hover:bg-primary/5'
                                    }`}>
                                <span
                                    className={`relative flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-N50 p-1
                                        transition-colors duration-200 md:h-10 md:w-10 ${isActive ? 'bg-primary/5' : 'group-hover:bg-primary/5'}`}>
                                    {item.image ? (
                                        <SafeImage
                                            src={item.image}
                                            fallback={FALLBACK_IMAGE}
                                            width={40}
                                            height={40}
                                            className='h-7 w-7 object-contain md:h-9 md:w-9'
                                            alt={item.label}
                                        />
                                    ) : (
                                        <Category2 size={20} color='currentColor' className={isActive ? 'text-primary' : 'text-blackLowEmphasis'} />
                                    )}
                                </span>
                                <Typography
                                    variant='caption'
                                    component='span'
                                    fontWeight={isActive ? 700 : 500}
                                    className={`line-clamp-2 text-right leading-4 ${isActive ? 'text-primary' : 'text-blackMediumEmphasis'}`}>
                                    {item.label}
                                </Typography>
                            </button>
                        );
                    })}
                </ScrollFade>
            ) : null}
        </div>
    );
};
